import { useQueryClient, UseQueryResult } from 'react-query';

import { ApiError, apiQueryKey, useApiMutation, useApiQuery } from '../useApi';
import {
    HelpArticle, HelpArticleFeedbackPayload, HelpArticlesQuery, HelpArticleSummary, HelpCategory, LabelledSupportHours, SupportConversation, SupportMessage,
} from './types';

const CONVERSATIONS_URL = '/support/conversations';
const UNREAD_URL = '/support/unread-count';
const conversationUrl = (id: number) => `${CONVERSATIONS_URL}/${id}`;

/** Les collections Laravel arrivent enveloppées dans `{ data: [...] }` : on rend le tableau. */
function unwrapList<T>(query: UseQueryResult<{ data: T[] }, ApiError>): UseQueryResult<T[], ApiError> {
    return { ...query, data: query.data?.data } as UseQueryResult<T[], ApiError>;
}

export const useSupportConversations = (enabled: boolean) =>
    unwrapList(useApiQuery<{ data: SupportConversation[] }>(enabled ? CONVERSATIONS_URL : '', {}, { enabled }));

/** Intervalle de rafraîchissement d'une conversation ouverte selon son état. */
export function supportConversationRefetchInterval(data?: SupportConversation): number | false {
    if (data?.assistant_pending) return 1500;   // l'assistant rédige sa réponse
    if (data?.status === 'waiting_human' || data?.status === 'human') return 20000;   // réponse de l'équipe attendue
    return false;
}

export const useSupportConversation = (id: number | null) =>
    useApiQuery<SupportConversation>(id ? conversationUrl(id) : '', {}, {
        refetchInterval: supportConversationRefetchInterval,
    });

export const useSupportUnreadCount = (enabled: boolean) =>
    useApiQuery<{ count: number }>(enabled ? UNREAD_URL : '', {}, {
        enabled,
        refetchInterval: 60000,
    });

/** Horaires de réponse de l'équipe (libellé déjà localisé côté serveur). */
export const useSupportHours = () =>
    useApiQuery<LabelledSupportHours>('/support/hours', {}, { staleTime: 10 * 60 * 1000 });

/** Invalide la liste, le compteur et, si connue, la conversation touchée. */
function useInvalidateSupport() {
    const queryClient = useQueryClient();
    return (conversationId?: number) => {
        if (conversationId) queryClient.invalidateQueries(apiQueryKey(conversationUrl(conversationId)));
        queryClient.invalidateQueries(apiQueryKey(CONVERSATIONS_URL));
        queryClient.invalidateQueries(apiQueryKey(UNREAD_URL));
    };
}

export const useStartSupportConversation = () => {
    const invalidate = useInvalidateSupport();
    return useApiMutation<SupportConversation, ApiError, { message: string; page_path: string }>(
        'post',
        () => CONVERSATIONS_URL,
        { onSuccess: (conversation) => invalidate(conversation.id) },
    );
};

export const useSendSupportMessage = () => {
    const invalidate = useInvalidateSupport();
    return useApiMutation<SupportMessage, ApiError, { conversationId: number; message: string; page_path: string }>(
        'post',
        ({ conversationId }) => `${conversationUrl(conversationId)}/messages`,
        { onSuccess: (_data, { conversationId }) => invalidate(conversationId) },
    );
};

export const useEscalateSupportConversation = () => {
    const invalidate = useInvalidateSupport();
    return useApiMutation<SupportConversation, ApiError, { conversationId: number }>(
        'post',
        ({ conversationId }) => `${conversationUrl(conversationId)}/escalate`,
        { onSuccess: (_data, { conversationId }) => invalidate(conversationId) },
    );
};

export const useMarkSupportConversationRead = () => {
    const invalidate = useInvalidateSupport();
    return useApiMutation<void, ApiError, { conversationId: number }>(
        'post',
        ({ conversationId }) => `${conversationUrl(conversationId)}/read`,
        { onSuccess: (_data, { conversationId }) => invalidate(conversationId) },
    );
};

export const useRateSupportMessage = () => {
    const queryClient = useQueryClient();
    return useApiMutation<SupportMessage, ApiError, { messageId: number; rating: 'up' | 'down' }>(
        'post',
        ({ messageId }) => `/support/messages/${messageId}/rating`,
        {
            onSuccess: () => {
                // Le message appartient à une conversation ouverte : on rafraîchit toutes les conversations en cache.
                queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(CONVERSATIONS_URL) });
            },
        },
    );
};

export const useHelpCategories = (enabled = true) =>
    unwrapList(useApiQuery<{ data: HelpCategory[] }>('/help-categories', {}, { keepPreviousData: true, enabled }));

/** `category` : slug, `none` (sans catégorie) ou absent. Sans `q`, l'ordre est celui du sommaire du centre d'aide. */
export const useHelpArticles = ({ q, category }: HelpArticlesQuery = {}, enabled = true) =>
    unwrapList(useApiQuery<{ data: HelpArticleSummary[] }>(
        '/help-articles',
        { params: { q: q || undefined, category: category || undefined } },
        { keepPreviousData: !category, enabled },
    ));

/** Les 6 articles les plus consultés (seuls ceux déjà lus). */
export const usePopularHelpArticles = (enabled = true) =>
    unwrapList(useApiQuery<{ data: HelpArticleSummary[] }>('/help-articles', { params: { sort: 'popular' } }, { enabled, staleTime: 5 * 60 * 1000 }));

const helpArticleUrl = (slug: string) => `/help-articles/${encodeURIComponent(slug)}`;

/** Chaque lecture compte une vue côté serveur : pas de rechargement au retour sur l'onglet. */
export const useHelpArticle = (slug: string) =>
    useApiQuery<HelpArticle>(slug ? helpArticleUrl(slug) : '', {}, { refetchOnWindowFocus: false });

export const useSendHelpFeedback = () => {
    const queryClient = useQueryClient();
    return useApiMutation<{ my_feedback: boolean }, ApiError, HelpArticleFeedbackPayload>(
        'post',
        ({ slug }) => `${helpArticleUrl(slug)}/feedback`,
        {
            onSuccess: (data, { slug }) => {
                queryClient.setQueryData<HelpArticle | undefined>(
                    apiQueryKey(helpArticleUrl(slug)),
                    (old) => (old ? { ...old, my_feedback: data.my_feedback } : old),
                );
            },
        },
    );
};
